/**
 * One table shape for all three modes.
 *
 * Solo and Tutorial hold a full engine `GameState`; Online only ever sees the
 * redacted `PlayerGameView` from the server. Both are adapted to this model, so
 * the table components cannot accidentally depend on hidden information.
 */

import { getLegalActions, getRuleSet } from "@kikik/mahjong-core";
import type {
  Action,
  TileKind,
  DiscardRef,
  GamePhase,
  GameResult,
  GameState,
  Meld,
  PlayerId,
  SeatWind,
  Tile,
} from "@kikik/mahjong-core";
import type { PlayerGameView } from "@kikik/game-protocol";

export interface TableSeat {
  id: PlayerId;
  seat: SeatWind;
  handCount: number;
  hasDrawnTile: boolean;
  discards: Tile[];
  melds: Meld[];
  isYou: boolean;
  displayName: string | null;
}

export interface TableClaims {
  discard: DiscardRef;
  waitingOn: PlayerId[];
  declared: { playerId: PlayerId; type: string }[];
}

export interface TableModel {
  you: PlayerId;
  phase: GamePhase;
  turn: PlayerId;
  dealer: PlayerId;
  wallRemaining: number;
  deadWallRemaining: number;
  lastDiscard: DiscardRef | null;
  pendingClaims: TableClaims | null;
  seats: TableSeat[];
  hand: Tile[];
  drawnTile: Tile | null;
  legalActions: Action[];
  result: GameResult | null;
  ruleSetId: string;
}

export function fromGameState(
  state: GameState,
  you: PlayerId,
  names: (playerId: PlayerId) => string | null = () => null,
): TableModel {
  return {
    you,
    phase: state.phase,
    turn: state.turn,
    dealer: state.dealer,
    wallRemaining: state.wall.length,
    deadWallRemaining: state.deadWall.length,
    lastDiscard: state.lastDiscard,
    pendingClaims: state.pendingClaims
      ? {
          discard: state.pendingClaims.discard,
          waitingOn: state.pendingClaims.waitingOn,
          declared: state.pendingClaims.declared.map((claim) => ({
            playerId: claim.playerId,
            type: claim.type,
          })),
        }
      : null,
    seats: state.players.map((player) => ({
      id: player.id,
      seat: player.seat,
      handCount: player.hand.length,
      hasDrawnTile: player.drawnTile !== null,
      discards: player.discards,
      melds: player.melds,
      isYou: player.id === you,
      displayName: names(player.id),
    })),
    hand: state.players[you].hand,
    drawnTile: state.players[you].drawnTile,
    legalActions: getLegalActions(state, you),
    result: state.result,
    ruleSetId: state.ruleSetId,
  };
}

export function fromPlayerView(
  view: PlayerGameView,
  names: (playerId: PlayerId) => string | null = () => null,
): TableModel {
  return {
    you: view.you,
    phase: view.phase,
    turn: view.turn,
    dealer: view.dealer,
    wallRemaining: view.wallRemaining,
    deadWallRemaining: view.deadWallRemaining,
    lastDiscard: view.lastDiscard,
    pendingClaims: view.pendingClaims
      ? {
          discard: view.pendingClaims.discard,
          waitingOn: view.pendingClaims.waitingOn,
          declared: view.pendingClaims.declared,
        }
      : null,
    seats: view.players.map((player) => ({
      id: player.id,
      seat: player.seat,
      handCount: player.handCount,
      hasDrawnTile: player.hasDrawnTile,
      discards: player.discards,
      melds: player.melds,
      isYou: player.id === view.you,
      displayName: names(player.id),
    })),
    hand: view.self.hand,
    drawnTile: view.self.drawnTile,
    legalActions: view.legalActions,
    result: view.result,
    ruleSetId: view.ruleSetId,
  };
}

/** Concealed tiles the player may discard right now. */
export function discardableIds(model: TableModel): Set<number> {
  const ids = new Set<number>();
  for (const action of model.legalActions) {
    if (action.type === "discard") ids.add(action.tileId);
  }
  return ids;
}

export function claimActions(model: TableModel): Action[] {
  return model.legalActions.filter(
    (action) =>
      action.type === "chow" ||
      action.type === "pong" ||
      action.type === "kong" ||
      action.type === "ron" ||
      action.type === "pass",
  );
}

export function isYourTurn(model: TableModel): boolean {
  if (model.result) return false;
  if (model.phase === "awaiting-claims") {
    return model.pendingClaims?.waitingOn.includes(model.you) ?? false;
  }
  return model.turn === model.you;
}

/** Tiles that would complete the hand right now, for the ready hint. */
export function computeWaits(model: TableModel): TileKind[] {
  const ruleSet = getRuleSet(model.ruleSetId);
  const you = model.seats.find((seat) => seat.isYou);
  const melds = you?.melds.length ?? 0;
  if (model.drawnTile !== null) return [];
  if (model.hand.length !== ruleSet.handSize - 3 * melds) return [];
  return ruleSet.findWaits(
    model.hand.map((tile) => tile.kind),
    melds,
  );
}

/**
 * Where a seat is drawn on the felt, from the point of view of `you`.
 *
 * You are always at the bottom. Play passes to the seat on your right, and the
 * seat on your left is the one whose discard you may chow - the same geometry
 * a real table has, so the picture matches the rules.
 */
export type SeatSide = "south" | "east" | "north" | "west";

const SIDES: readonly SeatSide[] = ["south", "east", "north", "west"];

export function seatSide(playerId: PlayerId, you: PlayerId): SeatSide {
  return SIDES[(playerId - you + 4) % 4] ?? "south";
}
